import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { BASE_URL } from "../../Utils/api";
import { Form, Button, Alert, InputGroup } from "react-bootstrap";
import { FiEye, FiEyeOff, FiMail, FiLock, FiSend } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import LoginSelecaoEntidade from "./LoginSelecaoEntidade";
import CriarContaModal from "./CriarContaModal";
import ModalAlterarSenhaObrigatoria from "./ModalAlterarSenhaObrigatoria";
import RecuperacaoSenhaModal from "./RecuperacaoSenhaModal";
import { GlobalContext } from "../../Utils/GlobalContext";
import { logVersaoAppNoConsole } from "../../Utils/versaoApp";
import { AMBIENTE_REMOTO } from "../../Utils/ambiente";
import "./Login.css";


const Login = () => {
  const { setGlobalData } = useContext(GlobalContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [needsActivation, setNeedsActivation] = useState(false);
  const [activationMessage, setActivationMessage] = useState("");
  const [isActivationLoading, setIsActivationLoading] = useState(false);
  const [showEntidadeModal, setShowEntidadeModal] = useState(false);
  const [entidades, setEntidades] = useState([]);
  const [showCriarContaModal, setShowCriarContaModal] = useState(false);
  const [googleRegistration, setGoogleRegistration] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showAlterarSenhaModal, setShowAlterarSenhaModal] = useState(false);
  const [userIdModal, setUserIdModal] = useState(null);
  const [pendingToken, setPendingToken] = useState(null);
  const [resetToken, setResetToken] = useState(
    () => new URLSearchParams(window.location.search).get("reset_token") || ""
  );
  const [showRecuperacaoSenha, setShowRecuperacaoSenha] = useState(
    () => Boolean(new URLSearchParams(window.location.search).get("reset_token"))
  );
  const loginDataPendente = useRef(null);
  const pendingAuthToken = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    logVersaoAppNoConsole();
  }, []);

  useEffect(() => {
    const originalPadding = document.body.style.paddingLeft;
    document.body.style.paddingLeft = "0";
    document.body.classList.add("login-page-body");
    document.body.classList.remove("dark-mode", "sidebar-collapsed");
    document.body.setAttribute("data-bs-theme", "light");

    return () => {
      document.body.style.paddingLeft = originalPadding;
      document.body.classList.remove("login-page-body");
    };
  }, []);

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const concluirLogin = useCallback(async (loginData, options = {}) => {
    const {
      token,
      userId,
      temNovidades,
      novidadesVersao: novidades,
      email: emailLogin,
    } = loginData;

    const emailUsuario = options.emailUsuario || emailLogin || email;

    // Não armazena o token ainda: se colocarmos em localStorage agora,
    // isAuthenticated() retorna true, AppInner re-renderiza e a rota /login
    // redireciona para "/" ANTES do modal de seleção de entidade aparecer.
    // O token vai para localStorage apenas em handleEntidadeSelect, após a seleção.
    localStorage.removeItem("authToken");
    localStorage.removeItem("entidade_id");
    localStorage.removeItem("selectedEntidade");
    pendingAuthToken.current = token;

    if (options.atualizarLembrarEmail) {
      if (rememberMe && emailUsuario) {
        localStorage.setItem("rememberedEmail", emailUsuario);
      } else {
        localStorage.removeItem("rememberedEmail");
      }
    }

    setGlobalData((prev) => ({
      ...prev,
      user_id: String(userId),
      user: emailUsuario,
      entidade_id: null,
    }));

    try {
      const entidadesResponse = await api.get(
        `${BASE_URL}/api/auth/entidades_usuario/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const lista = entidadesResponse.data.entidades || [];
      setEntidades(lista);

      if (Array.isArray(lista) && lista.length === 1) {
        const eid = String(lista[0].id);
        setGlobalData((prev) => ({
          ...prev,
          user_id: String(userId),
          user: emailUsuario,
        }));
        localStorage.setItem("entidade_id", eid);
        localStorage.setItem("selectedEntidade", eid);
      }

      if (temNovidades && Array.isArray(novidades) && novidades.length > 0) {
        setGlobalData((prev) => ({
          ...prev,
          temNovidades: true,
          novidadesVersao: novidades,
        }));
      }

      setShowEntidadeModal(true);
    } catch (entidadesErr) {
      console.error("Erro ao buscar entidades:", entidadesErr);
      setError(entidadesErr.response?.data?.message || "Erro ao buscar entidades do usuario.");
      localStorage.removeItem("authToken");
    }
  }, [email, rememberMe, setGlobalData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleError = params.get("google_error");
    const googleCode = params.get("google_code");
    const googleRegisterCode = params.get("google_register_code");

    if (!googleError && !googleCode && !googleRegisterCode) return;

    window.history.replaceState(null, "", window.location.pathname);

    if (googleError) {
      setError(googleError);
      return;
    }

    if (!AMBIENTE_REMOTO) {
      setError("Acesso com Google nao disponivel neste ambiente.");
      return;
    }

    const exchangeCode = googleCode || googleRegisterCode;
    setIsGoogleLoading(true);

    api.post(`${BASE_URL}/api/auth/google/exchange`, { code: exchangeCode }, { withCredentials: true })
      .then(async ({ data }) => {
        if (data.tipo === "login" && data.loginPayload) {
          await concluirLogin(data.loginPayload);
          return;
        }

        if (data.tipo === "register" && data.googleRegistration) {
          setGoogleRegistration(data.googleRegistration);
          document.body.classList.add("modal-open");
          setShowCriarContaModal(true);
          return;
        }

        setError("Retorno do Google nao reconhecido.");
      })
      .catch((err) => {
        console.error("Erro ao processar retorno Google:", err);
        setError(err.response?.data?.message || "Nao foi possivel concluir o acesso com Google.");
      })
      .finally(() => setIsGoogleLoading(false));
  }, [concluirLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedsActivation(false);
    setActivationMessage("");
    setIsLoading(true);

    try {
      const response = await api.post(`${BASE_URL}/api/auth/login`, { email, password }, { withCredentials: true });
      const loginData = response.data;

      if (loginData.trocarSenha === true) {
        loginDataPendente.current = loginData;
        setUserIdModal(String(loginData.userId));
        setPendingToken(loginData.token);
        setShowAlterarSenhaModal(true);
        return;
      }

      await concluirLogin(loginData, { emailUsuario: email, atualizarLembrarEmail: true });
    } catch (loginErr) {
      console.error("Erro de login:", loginErr);
      setError(loginErr.response?.data?.message || "Email ou senha incorretos. Tente novamente.");
      setNeedsActivation(
        loginErr.response?.data?.codigo ===
          "ENTIDADE_AGUARDANDO_ATIVACAO"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const reenviarAtivacao = async () => {
    setIsActivationLoading(true);
    setActivationMessage("");
    try {
      const { data } = await api.post(
        `${BASE_URL}/api/auth/reenviar-ativacao`,
        { email }
      );
      setActivationMessage(data?.message || "Solicitação registrada.");
    } catch {
      setActivationMessage(
        "Não foi possível solicitar o reenvio neste momento."
      );
    } finally {
      setIsActivationLoading(false);
    }
  };

  const fecharRecuperacaoSenha = () => {
    setShowRecuperacaoSenha(false);
    setResetToken("");
    const params = new URLSearchParams(window.location.search);
    params.delete("reset_token");
    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${query ? `?${query}` : ""}`
    );
  };

  const handleEntidadeSelect = (entidadeId) => {
    if (pendingAuthToken.current) {
      localStorage.setItem("authToken", pendingAuthToken.current);
      pendingAuthToken.current = null;
    }
    localStorage.setItem("selectedEntidade", entidadeId);
    localStorage.setItem("entidade_id", entidadeId);

    setGlobalData((prev) => ({
      ...prev,
      entidade_id: String(entidadeId),
    }));

    setShowEntidadeModal(false);
    navigate("/");
  };

  const iniciarGoogle = () => {
    setError("");
    setIsGoogleLoading(true);
    window.location.href = `${BASE_URL}/api/auth/google/start?mode=login`;
  };

  const abrirModalCriarConta = (e) => {
    e.preventDefault();
    setError("");
    setGoogleRegistration(null);
    document.body.classList.add("modal-open");
    setShowCriarContaModal(true);
  };

  const fecharModalCriarConta = () => {
    setShowCriarContaModal(false);
    setGoogleRegistration(null);
    setTimeout(() => {
      if (!document.querySelector(".modal.show")) {
        document.body.classList.remove("modal-open");
      }
    }, 300);
  };

  const deveExibirLinkAtivacao =
    needsActivation || /novo link/i.test(error || "");

  const renderMensagemAtivacao = () => {
    const mensagem = error || "";
    const partes = mensagem.split(/novo link/i);
    const link = (
      <a
        href="#reenviar-ativacao"
        className="login-activation-inline-link"
        aria-disabled={isActivationLoading || !email}
        onClick={(event) => {
          event.preventDefault();
          if (isActivationLoading || !email) return;
          reenviarAtivacao();
        }}
      >
        novo link
      </a>
    );

    if (partes.length < 2) {
      return (
        <>
          {mensagem} {link}
        </>
      );
    }

    return (
      <>
        {partes[0]}
        {link}
        {partes.slice(1).join("novo link")}
      </>
    );
  };

  const renderAlertaAtivacao = () => (
    <div className="login-activation-notice" role="alert">
      <div className="login-activation-icon" aria-hidden="true">
        <FiSend />
      </div>
      <div className="login-activation-content">
        <p>{renderMensagemAtivacao()}</p>
        {isActivationLoading && (
          <span className="login-activation-hint">
            Gerando um e-mail de ativação com nova validade...
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div id="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h2>Bem-vindo</h2>
            <p>Faca login para acessar o sistema</p>
          </div>

          {error && deveExibirLinkAtivacao && renderAlertaAtivacao()}
          {error && !deveExibirLinkAtivacao && (
            <Alert variant="danger" className="login-alert">
              {error}
            </Alert>
          )}
          {deveExibirLinkAtivacao && activationMessage && (
            <div className="login-activation-box">
              <Alert variant="info" className="login-activation-message">
                {activationMessage}
              </Alert>
            </div>
          )}

          <Form onSubmit={handleSubmit} className="login-form">
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FiMail />
                </InputGroup.Text>
                <Form.Control
                  type="email"
                  placeholder="Seu email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Senha</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FiLock />
                </InputGroup.Text>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                  type="button"
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="login-form-options mb-4">
              <Form.Check
                type="checkbox"
                id="rememberMe"
                label="Lembrar-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <Button
                type="button"
                variant="link"
                className="login-forgot-link"
                onClick={() => setShowRecuperacaoSenha(true)}
                disabled={isLoading || isGoogleLoading}
              >
                Esqueci minha senha
              </Button>
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="login-button"
              disabled={isLoading || isGoogleLoading}
            >
              {isLoading ? "Entrando..." : "Entrar"}
            </Button>
          </Form>

          {AMBIENTE_REMOTO && (
            <div className="login-footer">
              <p>
                Ainda não tem uma conta?{" "}
                <Button
                  variant="link"
                  className="p-0 login-footer-link"
                  onClick={abrirModalCriarConta}
                  disabled={isLoading || isGoogleLoading}
                >
                  Criar Conta
                </Button>
              </p>
            </div>
          )}

          {AMBIENTE_REMOTO && (
            <>
              <div className="login-divider">
                <span>Ou prossiga com</span>
              </div>

              <div className="login-providers">
                <Button
                  type="button"
                  variant="light"
                  className="login-provider-button"
                  onClick={iniciarGoogle}
                  disabled={isLoading || isGoogleLoading}
                >
                  <FcGoogle className="login-provider-icon" />
                  {isGoogleLoading ? "Conectando..." : "Google"}
                </Button>
                {/* Futuros provedores (Microsoft, Apple...) podem ser adicionados aqui */}
              </div>
            </>
          )}
        </div>
      </div>

      <LoginSelecaoEntidade
        show={showEntidadeModal}
        entidades={entidades}
        handleSelect={handleEntidadeSelect}
      />

      <ModalAlterarSenhaObrigatoria
        show={showAlterarSenhaModal}
        userId={userIdModal}
        token={pendingToken}
        onSucesso={async () => {
          setShowAlterarSenhaModal(false);
          const loginData = loginDataPendente.current;
          loginDataPendente.current = null;
          setPendingToken(null);
          await concluirLogin(loginData, { emailUsuario: email, atualizarLembrarEmail: true });
        }}
      />
      <CriarContaModal
        show={showCriarContaModal}
        onHide={fecharModalCriarConta}
        googleRegistration={googleRegistration}
      />
      <RecuperacaoSenhaModal
        show={showRecuperacaoSenha}
        onHide={fecharRecuperacaoSenha}
        emailInicial={email}
        token={resetToken}
      />
    </div>
  );
};

export default Login;
